import { Button, Stack, TextField, MenuItem, Typography, Box, FormLabel } from '@mui/material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Title } from '../../../components/Atoms/Title';
import styles from './styles';
import { postContactAuthed } from '../requests';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

interface FormValues {
  title: string;
  content: string;
  // 他のフィールドも必要に応じてここに追加します。
}

const schema = yup.object({
  title: yup.string().required('必須です'),
  content: yup.string().required('必須です'),
});

const ContactPage: React.FC = () => {
  const [t] = useTranslation();
  const title = React.useMemo(() => {
    return [
      {
        value: t('pages.appContact.const.title.a.value'),
        label: t('pages.appContact.const.title.a.label'),
      },
      {
        value: t('pages.appContact.const.title.c.value'),
        label: t('pages.appContact.const.title.c.label'),
      },
      {
        value: t('pages.appContact.const.title.d.value'),
        label: t('pages.appContact.const.title.d.label'),
      },
    ];
  }, [t]);

  const [data, setData] = useState<FormValues | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const toConfirm = (data: FormValues) => {
    // GA4: お問い合わせ確認画面への遷移を計測（本文は送信しない）
    sendAnalyticsEvent('contact_form', {
      action: 'confirm',
      title_type: data.title,
      content_length: data.content.length,
    });
    setData(data);
    setShowConfirm(true);
  };

  const toContact = () => {
    // GA4: お問い合わせ確認画面から編集へ戻る操作を計測
    sendAnalyticsEvent('contact_form', { action: 'back_to_edit' });
    setShowConfirm(false);
  };

  const onSubmit = async () => {
    try {
      await postContactAuthed({ data });
      // GA4: お問い合わせ送信成功を計測
      sendAnalyticsEvent('contact_form', {
        action: 'submit_success',
        title_type: data?.title,
      });
    } catch (error) {
      // GA4: お問い合わせ送信失敗を計測
      sendAnalyticsEvent('contact_form', {
        action: 'submit_failure',
        title_type: data?.title,
      });
      throw error;
    }
  };

  return (
    <Box style={{ padding: '24px 0px 0px 24px' }}>
      <Title variant="h2">{t('pages.appContact.title')}</Title>
      <Stack spacing={3} sx={{ width: 630, textAlign: 'left', margin: '32px auto' }}>
        {!showConfirm ? (
          <>
            <Stack spacing={1}>
              <FormLabel required sx={styles.formLabel}>
                {t('common.label.information')}
              </FormLabel>
              <TextField
                required
                select
                defaultValue={data?.title || ''}
                {...register('title')}
                error={'title' in errors}
                helperText={errors.title?.message}
                SelectProps={{
                  sx: { ...styles.textField, width: 290, textAlign: 'left' },
                  SelectDisplayProps: {
                    style: { padding: 0, lineHeight: '30px' },
                  },
                }}
              >
                <MenuItem disabled value=""></MenuItem>
                {title.map(option => (
                  <MenuItem sx={{ textAlign: 'left' }} key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack spacing={1}>
              <FormLabel required sx={styles.formLabel}>
                {t('common.label.contents')}
              </FormLabel>
              <TextField
                required
                multiline
                rows={10}
                {...register('content')}
                error={'content' in errors}
                helperText={errors.content?.message}
              />
            </Stack>

            <Stack spacing={1} style={{ alignItems: 'center' }}>
              <Box>{t('pages.appContact.alert.content')}</Box>

              <Button
                onClick={handleSubmit(toConfirm)}
                sx={{
                  ...styles.button,
                  backgroundColor: '#2980B9',
                }}
              >
                {t('common.button.detail')}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Stack spacing={1}>
              <FormLabel sx={styles.formLabel}>{t('common.label.information')}</FormLabel>
              <Typography sx={styles.confirmValue}>{data?.title}</Typography>
            </Stack>

            <Stack spacing={1}>
              <FormLabel sx={styles.formLabel}>{t('common.label.contents')}</FormLabel>
              <Typography sx={styles.confirmValue}>{data?.content}</Typography>
            </Stack>

            <Stack
              spacing={1}
              style={{ justifyContent: 'center', marginTop: '60px' }}
              direction="row"
            >
              <Button onClick={onSubmit} sx={styles.button}>
                {t('common.button.send')}
              </Button>
              <Button
                onClick={toContact}
                variant="outlined"
                sx={{ ...styles.button, ...styles.outline }}
                style={{ marginLeft: '48px' }}
              >
                {t('common.button.edit')}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
    // JSX の構造は基本的に変更無しですが、型注釈を適用した変更を反映します。
  );
};

export default ContactPage;
